import React, { useCallback, useState } from "react";
import { keyframes } from "styled-components";

interface InfiniteSlideProps {
  className?: string;
  itemWidth: number;
}

const InfiniteSlide: React.FC<InfiniteSlideProps> = ({
  className,
  itemWidth,
  children,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const count = React.Children.count(children);
  // We need to repeat the contents, with aria-hidden set to "true",
  // to give illusion of infinite stream.
  const duplicate = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement, { hidden: true });
    }
    return null;
  });

  const handleOnTouchStart = useCallback(() => {
    setIsPaused(true);
  }, []);

  const handleOnTouchEnd = useCallback(() => {
    setIsPaused(false);
  }, []);

  return (
    <div
      css={`
        overflow: hidden;
        user-select: none;
      `}
      className={className}
      onTouchStart={handleOnTouchStart}
      onTouchEnd={handleOnTouchEnd}
    >
      <div
        css={`
          display: grid;
          grid-template-columns: repeat(${2 * count}, ${itemWidth}px);
          column-gap: 20px;
          transform: translateZ(0);
          animation: ${slideKeyframes(count * (itemWidth + 20))}
            ${(count * (itemWidth + 20)) / 100}s linear infinite;
          animation-play-state: ${isPaused ? "paused" : "running"};
        `}
      >
        {children}
        {duplicate}
      </div>
    </div>
  );
};

export default InfiniteSlide;

const slideKeyframes = (width: number) => {
  const slide = keyframes`
    100% {
      transform: translateX(${-1 * width}px);
    }
  `;
  return slide;
};
